import React, { useState } from 'react';

import { DeactivatePaperPlansInput } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import onCheckTokens from '@amzn/meridian-tokens/base/icon/check-circle';
import offCheckTokens from '@amzn/meridian-tokens/base/icon/close-circle';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import infoTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import deleteTokens from '@amzn/meridian-tokens/base/icon/trash';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import { isUndefined } from 'lodash';
import { useHistory } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { DEFAULT_TOASTER_TIMEOUT, PlanWeightsTab } from 'src/constants';
import { EDIT_PLAN_ALERT } from 'src/constants/alertMessages';
import { ADMIN_ROLE_SET, EDITOR_ROLE_SET } from 'src/constants/role';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IPlanValues } from 'src/types/IPaperPlan';
import { IToast } from 'src/types/IToaster';
import { IWeightsProfile } from 'src/types/IWeightsProfile';
import {
    getPlanGroup,
    getPlanYYYYMMDDUTCDate,
    getPromoDurationDisplayNumber,
    getRateGroup,
    getTerritory,
    isPromoPlan
} from 'src/util/PaperPlans/PaperPlanUtils';

import { getWeightsFieldFromMap, getWeightsInputs } from '../../AddPaperPlan/AddWeights/addWeightsUtils';
import { TerritoryFlag } from '../../TerritoryFlag';
import './PlanDetail.scss';

interface IPlanDetailProps {
    heading: string;
    planValues: IPlanValues;
    hasEditCancelBtn?: boolean;
    hasAlert?: boolean;
    planWeights?: Map<PlanWeightsTab, IWeightsProfile>;
}

export const PlanDetail = ({
    heading,
    planValues,
    hasEditCancelBtn = false,
    hasAlert = false,
    planWeights
}: IPlanDetailProps) => {
    const history = useHistory();

    const [isCancelPlanModalOpen, setIsCancelPlanModalOpen] = React.useState(false);
    const [cancelPlanModalComment, setCancelPlanModalComment] = React.useState<string>('');

    const { userRole, userInfo, setToasts, setTopAlertMsg } = React.useContext(AppContext);

    const planOnIcon = <Icon className="on-check" tokens={onCheckTokens} />;
    const planOffIcon = <Icon className="off-check" tokens={offCheckTokens} />;

    const [currentTab, setCurrentTab] = useState(Object.values(PlanWeightsTab)[0]);

    const handleCancelPlan = () => {
        contractConfigurationClient
            .deactivatePaperPlans(
                DeactivatePaperPlansInput.clone({
                    changeBy: userInfo.username,
                    paperPlanIds: [Number(planValues.paperPlanId)]
                })
            )
            .subscribe(
                () => {
                    const toast: IToast = {
                        id: 'cancel-plan-toast',
                        timeout: DEFAULT_TOASTER_TIMEOUT,
                        message: 'Plan canceled. Please note that this could require reruns of past accounting periods.'
                    };
                    setToasts([toast]);
                    history.push(ROUTES.PAPER_PLANS.INDEX);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('deactivatePaperPlans Unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    };

    return (
        <Column spacing="300" maxWidth="550px">
            <Heading level={1}>{heading}</Heading>

            {hasAlert && <Alert type="warning">{EDIT_PLAN_ALERT}</Alert>}

            <Column spacingInset="500 none none none" spacing="400">
                <Column spacing="200">
                    <Text type="h200">{planValues.paperPlanName}</Text>
                    <Text>Display name: {planValues.displayName}</Text>
                    <Text>Reporting name: {planValues.reportingName}</Text>
                    <Text>Subscription product: {planValues.subscriptionProduct}</Text>
                    <Text>Promo affiliate name: {planValues.affiliateName}</Text>
                    <Text className="description-text">Description: {planValues.description}</Text>
                </Column>
                <Divider />
                <Row alignmentHorizontal="justify" alignmentVertical="top">
                    <Column spacing="400">
                        <Column spacing="200">
                            <Text tag="label" type="h100">
                                Plan type
                            </Text>
                            <Text>{planValues.planType}</Text>
                        </Column>

                        <Column spacing="200">
                            <Text tag="label" type="h100">
                                Territory
                            </Text>
                            <TerritoryFlag territoryCode={getTerritory(planValues.territory)} />
                        </Column>

                        <Column spacing="200">
                            <Text tag="label" type="h100">
                                Start date
                            </Text>
                            <Text>{getPlanYYYYMMDDUTCDate(planValues.startDate)}</Text>
                        </Column>

                        {planValues.endDate && (
                            <Column spacing="200">
                                <Text tag="label" type="h100">
                                    End date
                                </Text>
                                <Text>{getPlanYYYYMMDDUTCDate(planValues.endDate, true)}</Text>
                            </Column>
                        )}

                        {planValues.renewalCadence && (
                            <Column spacing="200">
                                <Text tag="label" type="h100">
                                    Renewal cadence
                                </Text>
                                <Text>{planValues.renewalCadence}</Text>
                            </Column>
                        )}

                        {planValues.promoDurationInDays && (
                            <Column spacing="200">
                                <Text tag="label" type="h100">
                                    Promo Duration
                                </Text>
                                <Text>{getPromoDurationDisplayNumber(planValues.promoDurationInDays) + ' days'}</Text>
                            </Column>
                        )}

                        {planValues.wholesalePrice && (
                            <Column spacing="200">
                                <Text tag="label" type="h100">
                                    Wholesale price
                                </Text>
                                <Text>
                                    {planValues.wholesalePrice} {planValues.wholesalePriceCurrency}
                                </Text>
                            </Column>
                        )}

                        {planValues.endUserPrice && (
                            <Column spacing="200">
                                <Text tag="label" type="h100">
                                    End user price
                                </Text>
                                <Text>
                                    {planValues.endUserPrice} {planValues.endUserPriceCurrency}
                                </Text>
                            </Column>
                        )}

                        {isPromoPlan(planValues) && planValues.isBundle && planValues.promoMultiplier && (
                            <Column spacing="200">
                                <Text tag="label" type="h100">
                                    Promo Multiplier
                                </Text>
                                <Text>{planValues.promoMultiplier}</Text>
                            </Column>
                        )}
                    </Column>

                    <Column className="gray-column" width={300} spacingInset="400" spacing="300">
                        <Row alignmentHorizontal="justify" spacingInset="200 none">
                            <Text tag="label" type="h100">
                                Plan group
                            </Text>
                            <Text>{getPlanGroup(planValues)}</Text>
                        </Row>

                        <Row alignmentHorizontal="justify" spacingInset="200 none">
                            <Text tag="label" type="h100">
                                Rate group
                            </Text>
                            <Text>{getRateGroup(planValues)}</Text>
                        </Row>

                        <Divider />

                        <Row alignmentHorizontal="justify" spacingInset="200 none">
                            <Text tag="label">Carrier plan</Text>
                            {planValues.carrierPlan ? planOnIcon : planOffIcon}
                        </Row>

                        <Row alignmentHorizontal="justify" spacingInset="200 none">
                            <Text tag="label">Royalty bearing</Text>
                            {planValues.royaltyBearing ? planOnIcon : planOffIcon}
                        </Row>

                        <Row alignmentHorizontal="justify" spacingInset="200 none">
                            <Text tag="label">Customer pays</Text>
                            {planValues.customerPays ? planOnIcon : planOffIcon}
                        </Row>

                        <Row alignmentHorizontal="justify" spacingInset="200 none">
                            <Text tag="label">Is promotion</Text>
                            {planValues.customerPays && planValues.royaltyBearing && planValues.renewalCadence
                                ? planOffIcon
                                : planOnIcon}
                        </Row>

                        <Row alignmentHorizontal="justify" spacingInset="200 none">
                            <Text tag="label">Is bundle</Text>
                            {planValues.isBundle ? planOnIcon : planOffIcon}
                        </Row>
                    </Column>
                </Row>
                <Divider />
                {!isUndefined(planWeights) && (
                    <Column>
                        <Heading level={3}>Plan Weights</Heading>
                        <TabGroup value={currentTab} onChange={setCurrentTab} fill="line">
                            {Array.from(planWeights.keys()).map((tab) => (
                                <Tab value={tab} key={tab} id={tab} data-testid={tab}>
                                    {tab}
                                </Tab>
                            ))}
                        </TabGroup>
                        {getWeightsInputs(currentTab).map((input) => (
                            <Box key={input.name} type="outline" spacingInset="400">
                                <Row alignmentHorizontal="justify">
                                    <Text type="h200">{input.name}</Text>
                                    <Row alignmentVertical="top">
                                        {input.inputValues.map((inputValue) => (
                                            <Column
                                                key={`${input.name}-${inputValue}`}
                                                spacing="200"
                                                alignmentHorizontal="end"
                                            >
                                                <Text tag="label" type="h100">
                                                    {inputValue}
                                                </Text>
                                                <Text key={`${input.name}-${inputValue}`}>
                                                    {getWeightsFieldFromMap(
                                                        planWeights,
                                                        currentTab,
                                                        input.name,
                                                        inputValue
                                                    )}
                                                </Text>
                                            </Column>
                                        ))}
                                    </Row>
                                </Row>
                            </Box>
                        ))}
                    </Column>
                )}

                {hasEditCancelBtn && (
                    <Row spacing="200" alignmentHorizontal="end">
                        <Row spacing="none">
                            <Button
                                type="link"
                                size="small"
                                href={
                                    planValues.isDraft || ADMIN_ROLE_SET.has(userRole)
                                        ? `${ROUTES.PAPER_PLANS.INDEX}${ROUTES.PAPER_PLANS.EDIT}/${planValues.paperPlanId}${ROUTES.PAPER_PLANS.STEP.SETTINGS}`
                                        : `${ROUTES.PAPER_PLANS.INDEX}${ROUTES.PAPER_PLANS.EDIT}/${planValues.paperPlanId}${ROUTES.PAPER_PLANS.STEP.DETAILS}`
                                }
                                onClick={history.push}
                                disabled={!EDITOR_ROLE_SET.has(userRole)}
                            >
                                <Icon className="button-icon" tokens={editTokens} />
                                Edit plan
                            </Button>
                            <Button
                                className="red-button"
                                type="link"
                                size="small"
                                onClick={() => setIsCancelPlanModalOpen(true)}
                                disabled={
                                    planValues.isDeleted ||
                                    (planValues.isDraft && !EDITOR_ROLE_SET.has(userRole)) ||
                                    !ADMIN_ROLE_SET.has(userRole)
                                }
                            >
                                <Icon className="button-icon" tokens={deleteTokens} />
                                Cancel plan
                            </Button>
                            {!EDITOR_ROLE_SET.has(userRole) && (
                                <Tooltip position="bottom" title="You do not have permissions for this action">
                                    <Button className="info-button" type="link" size="small">
                                        <Icon className="info-button-icon" tokens={infoTokens} />
                                    </Button>
                                </Tooltip>
                            )}
                        </Row>
                    </Row>
                )}
            </Column>

            <ConfirmationModal
                isOpen={isCancelPlanModalOpen}
                onClose={() => setIsCancelPlanModalOpen(false)}
                title="Cancel plan"
                body="Are you sure you want to cancel this plan?"
                comments={cancelPlanModalComment}
                showComments={true}
                onCommentsChange={setCancelPlanModalComment}
                primaryButtonText="Cancel plan"
                primaryButtonOnClick={handleCancelPlan}
                secondaryButtonText="Go back"
                secondaryButtonOnClick={() => setIsCancelPlanModalOpen(false)}
            />
        </Column>
    );
};
